import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/stores/auth';
import { api } from '@/lib/api/client';
import { getActivityNavigation } from '@/lib/utils/navigation-helpers';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { UserHoverCard } from '@/components/ui/user-hover-card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Activity,
  Play,
  MessageCircle,
  Heart,
  Eye,
  Clock,
  Filter,
  Download,
  RefreshCw,
  User,
} from 'lucide-react';

type ActivityType =
  | 'login'
  | 'logout'
  | 'stream_view'
  | 'comment'
  | 'invite_used'
  | 'register'
  | 'qa'
  | 'activity'
  | 'like'
  | 'share';

interface ActivityItem {
  id: string;
  user: {
    id: string;
    name: string;
    avatar?: string;
    role: string;
  };
  type: ActivityType;
  description: string;
  timestamp: string;
  metadata?: {
    streamId?: string;
    streamTitle?: string;
    commentId?: string;
    inviteCode?: string;
  };
}

interface UserActivityTimelineProps {
  userId: string | number;
  activities?: ActivityItem[];
  showFilters?: boolean;
  maxItems?: number;
}

interface ActivityApiItem {
  id?: string | number;
  type?: string;
  description?: string;
  timestamp?: string;
  user?: {
    id?: string | number;
    name?: string;
    avatar?: string;
    role?: string;
  };
  metadata?: {
    streamId?: string | number;
    streamTitle?: string;
    commentId?: string | number;
    inviteCode?: string;
    details?: Record<string, unknown>;
  };
}

interface ActivityApiResponse {
  activities: ActivityApiItem[];
}

interface ActivitySummary {
  totalActivities: number;
  uniqueUsers: number;
  mostActiveType: ActivityType;
  avgActivitiesPerHour: number;
}

function asString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim().length > 0) return value;
  if (typeof value === 'number') return String(value);
  return undefined;
}

function normalizeActivityType(type: string | undefined): ActivityType {
  switch (type) {
    case 'login':
      return 'login';
    case 'logout':
      return 'logout';
    case 'stream':
    case 'stream_view':
      return 'stream_view';
    case 'comment':
      return 'comment';
    case 'invite':
    case 'invite_used':
      return 'invite_used';
    case 'register':
      return 'register';
    case 'qa':
      return 'qa';
    case 'like':
      return 'like';
    case 'share':
      return 'share';
    default:
      return 'activity';
  }
}

function mapApiActivity(activity: ActivityApiItem, fallbackIndex: number): ActivityItem {
  const metadataDetails = activity.metadata?.details || {};
  const streamId = asString(activity.metadata?.streamId) || asString(metadataDetails.streamId);
  const streamTitle = asString(activity.metadata?.streamTitle) || asString(metadataDetails.streamTitle);
  const commentId = asString(activity.metadata?.commentId) || asString(metadataDetails.commentId);
  const inviteCode = asString(activity.metadata?.inviteCode) || asString(metadataDetails.inviteCode);

  return {
    id: asString(activity.id) || `activity-${fallbackIndex}`,
    user: {
      id: asString(activity.user?.id) || 'system',
      name: activity.user?.name || 'System',
      avatar: activity.user?.avatar,
      role: activity.user?.role || 'member',
    },
    type: normalizeActivityType(activity.type),
    description: activity.description || 'Activity recorded',
    timestamp: activity.timestamp || new Date().toISOString(),
    metadata: {
      streamId,
      streamTitle,
      commentId,
      inviteCode,
    },
  };
}

function transformApiActivities(data: ActivityApiResponse): ActivityItem[] {
  return (data.activities || []).map((activity, index) => mapApiActivity(activity, index));
}

async function fetchActivitiesFromApi(): Promise<ActivityItem[]> {
  const response = await api.get<ActivityApiResponse>('/api/admin/analytics/activity');
  if (!response.success || !response.data) {
    throw new Error(response.error?.message || 'Failed to fetch user activities');
  }
  return transformApiActivities(response.data);
}

function withinTimeRange(timestamp: string, timeRange: string): boolean {
  const activityTime = new Date(timestamp).getTime();
  if (Number.isNaN(activityTime)) return false;

  const now = Date.now();
  const rangesMs: Record<string, number> = {
    '1h': 60 * 60 * 1000,
    '24h': 24 * 60 * 60 * 1000,
    '7d': 7 * 24 * 60 * 60 * 1000,
    '30d': 30 * 24 * 60 * 60 * 1000,
  };

  const limitMs = rangesMs[timeRange] || rangesMs['24h'];
  return now - activityTime <= limitMs;
}

function getActivitySummary(activities: ActivityItem[]): ActivitySummary {
  const counts = activities.reduce<Record<ActivityType, number>>((acc, activity) => {
    acc[activity.type] = (acc[activity.type] || 0) + 1;
    return acc;
  }, {
    login: 0,
    logout: 0,
    stream_view: 0,
    comment: 0,
    invite_used: 0,
    register: 0,
    qa: 0,
    activity: 0,
    like: 0,
    share: 0,
  });

  let mostActiveType: ActivityType = 'activity';
  let maxCount = -1;
  for (const [type, count] of Object.entries(counts)) {
    if (count > maxCount) {
      mostActiveType = type as ActivityType;
      maxCount = count;
    }
  }

  return {
    totalActivities: activities.length,
    uniqueUsers: new Set(activities.map((activity) => activity.user.id)).size,
    mostActiveType,
    avgActivitiesPerHour: Math.round(activities.length / 24),
  };
}

function getActivityIcon(type: ActivityItem['type']) {
  switch (type) {
    case 'login':
    case 'logout':
    case 'register':
      return <User className="h-4 w-4 text-blue-500" />;
    case 'stream_view':
      return <Play className="h-4 w-4 text-green-500" />;
    case 'comment':
    case 'qa':
      return <MessageCircle className="h-4 w-4 text-purple-500" />;
    case 'like':
      return <Heart className="h-4 w-4 text-red-500" />;
    case 'share':
      return <Eye className="h-4 w-4 text-orange-500" />;
    case 'invite_used':
      return <Activity className="h-4 w-4 text-indigo-500" />;
    default:
      return <Activity className="h-4 w-4 text-gray-500" />;
  }
}

function getActivityColor(type: ActivityItem['type']) {
  switch (type) {
    case 'login':
    case 'logout':
    case 'register':
      return 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400';
    case 'stream_view':
      return 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400';
    case 'comment':
    case 'qa':
      return 'bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400';
    case 'like':
      return 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400';
    case 'share':
      return 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400';
    case 'invite_used':
      return 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-400';
    default:
      return 'bg-gray-50 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400';
  }
}

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;

  return date.toLocaleDateString();
}

export function UserActivityTimeline({
  userId,
  activities: propActivities,
  showFilters = true,
  maxItems = 50,
}: UserActivityTimelineProps) {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [timeRange, setTimeRange] = useState<string>('24h');
  const { isInitialized } = useAuthStore();

  useEffect(() => {
    if (!isInitialized) return;

    const loadActivities = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (propActivities) {
          setActivities(propActivities);
          return;
        }

        const fetchedActivities = await fetchActivitiesFromApi();
        setActivities(fetchedActivities);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load user activities');
        setActivities([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadActivities();
  }, [userId, isInitialized, propActivities]);

  const filteredActivities = activities
    .filter((activity) => {
      if (filter !== 'all' && activity.type !== filter) return false;
      return withinTimeRange(activity.timestamp, timeRange);
    })
    .slice(0, maxItems);

  const handleRefresh = async () => {
    if (!isInitialized || propActivities) return;

    try {
      setIsLoading(true);
      setError(null);

      const fetchedActivities = await fetchActivitiesFromApi();
      setActivities(fetchedActivities);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh user activities');
      setActivities([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = () => {
    if (filteredActivities.length === 0) return;

    const exportData = {
      exportedAt: new Date().toISOString(),
      timeRange,
      filter,
      activities: filteredActivities,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `activity-timeline-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              User Activity Timeline
            </CardTitle>
            <CardDescription>
              Real-time user engagement and platform activity
            </CardDescription>
          </div>

          <div className="flex items-center space-x-2">
            {showFilters && (
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-36">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Activity</SelectItem>
                  <SelectItem value="login">Logins</SelectItem>
                  <SelectItem value="logout">Logouts</SelectItem>
                  <SelectItem value="register">Registrations</SelectItem>
                  <SelectItem value="stream_view">Stream Views</SelectItem>
                  <SelectItem value="comment">Comments</SelectItem>
                  <SelectItem value="invite_used">Invites</SelectItem>
                </SelectContent>
              </Select>
            )}

            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1h">1 hour</SelectItem>
                <SelectItem value="24h">24 hours</SelectItem>
                <SelectItem value="7d">7 days</SelectItem>
                <SelectItem value="30d">30 days</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading || Boolean(propActivities)}>
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>

            <Button variant="outline" size="sm" onClick={handleExport} disabled={filteredActivities.length === 0}>
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <ScrollArea className="h-96">
          <div className="space-y-4">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-start gap-4 p-3 rounded-lg">
                  <Skeleton className="w-8 h-8 rounded-full" />
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-16 rounded-full" />
                    </div>
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-3/4" />
                    <div className="flex gap-2">
                      <Skeleton className="h-4 w-12" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                  </div>
                  <Skeleton className="h-3 w-16" />
                </div>
              ))
            ) : filteredActivities.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No activity found for the selected filters</p>
              </div>
            ) : (
              filteredActivities.map((activity, index) => {
                const targetUrl = getActivityNavigation(activity.type, activity.metadata);
                const isClickable = targetUrl !== null;

                return (
                  <div
                    key={activity.id}
                    className={`flex items-start gap-4 p-3 rounded-lg transition-colors ${
                      isClickable ? 'cursor-pointer hover:bg-muted/70' : 'hover:bg-muted/50'
                    }`}
                    onClick={isClickable ? () => {
                      if (targetUrl) {
                        navigate(targetUrl);
                      }
                    } : undefined}
                    role={isClickable ? 'button' : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    onKeyDown={isClickable ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (targetUrl) {
                          navigate(targetUrl);
                        }
                      }
                    } : undefined}
                    aria-label={isClickable ? `Navigate to ${activity.description}` : undefined}
                  >
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full bg-background border-2 border-primary/20 flex items-center justify-center">
                        {getActivityIcon(activity.type)}
                      </div>
                      {index < filteredActivities.length - 1 && (
                        <div className="w-px h-8 bg-border mt-2" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <UserHoverCard user={{ name: activity.user.name, avatar: activity.user.avatar, role: activity.user.role, lastActive: activity.timestamp }}>
                              <Avatar className="h-6 w-6">
                                <AvatarImage src={activity.user.avatar} />
                                <AvatarFallback className="text-xs">
                                  {activity.user.name.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                            </UserHoverCard>
                            <span className="font-medium text-sm">{activity.user.name}</span>
                            <Badge
                              variant="outline"
                              className={`text-xs capitalize ${getActivityColor(activity.type)}`}
                            >
                              {activity.type.replace('_', ' ')}
                            </Badge>
                          </div>

                          <p className="text-sm text-muted-foreground mb-2">
                            {activity.description}
                          </p>

                          {activity.metadata && (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {activity.metadata.streamTitle && (
                                <Badge variant="secondary" className="text-xs">
                                  {activity.metadata.streamTitle}
                                </Badge>
                              )}
                              {activity.metadata.inviteCode && (
                                <Badge variant="secondary" className="text-xs">
                                  Code: {activity.metadata.inviteCode}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {formatTimestamp(activity.timestamp)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>

        {error && (
          <div className="mt-4 text-sm text-destructive">{error}</div>
        )}

        <div className="mt-6 pt-4 border-t">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-600">
                {activities.filter((activity) => activity.type === 'login').length}
              </div>
              <div className="text-xs text-muted-foreground">Logins</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">
                {activities.filter((activity) => activity.type === 'stream_view').length}
              </div>
              <div className="text-xs text-muted-foreground">Stream Views</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">
                {activities.filter((activity) => activity.type === 'comment').length}
              </div>
              <div className="text-xs text-muted-foreground">Comments</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-indigo-600">
                {activities.filter((activity) => activity.type === 'invite_used').length}
              </div>
              <div className="text-xs text-muted-foreground">Invites</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function ActivityStats({ className }: { className?: string }) {
  const [stats, setStats] = React.useState<ActivitySummary>({
    totalActivities: 0,
    uniqueUsers: 0,
    mostActiveType: 'activity',
    avgActivitiesPerHour: 0,
  });

  useEffect(() => {
    let mounted = true;

    const loadStats = async () => {
      try {
        const activities = await fetchActivitiesFromApi();
        if (!mounted) return;
        setStats(getActivitySummary(activities));
      } catch {
        if (!mounted) return;
        setStats({
          totalActivities: 0,
          uniqueUsers: 0,
          mostActiveType: 'activity',
          avgActivitiesPerHour: 0,
        });
      }
    };

    loadStats();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 ${className}`}>
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-blue-500" />
            <div>
              <div className="text-lg font-semibold">{stats.totalActivities}</div>
              <div className="text-xs text-muted-foreground">Total Activities</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-green-500" />
            <div>
              <div className="text-lg font-semibold">{stats.uniqueUsers}</div>
              <div className="text-xs text-muted-foreground">Active Users</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <Play className="h-4 w-4 text-purple-500" />
            <div>
              <div className="text-lg font-semibold">{stats.avgActivitiesPerHour}</div>
              <div className="text-xs text-muted-foreground">Per Hour</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <Heart className="h-4 w-4 text-red-500" />
            <div>
              <div className="text-lg font-semibold capitalize">
                {stats.mostActiveType.replace('_', ' ')}
              </div>
              <div className="text-xs text-muted-foreground">Top Activity</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
